import type { AcknowledgeEtlHaltResponse, EtlHalt, EtlStorageCapacity } from '../types/etlHalts'
import { fetchApi } from './api'

/**
 * ETL halts (`EtlHaltController`, `api/etl/halts`).
 *
 * Three calls, three gates on the server:
 * - the halt list is served to any signed-in user;
 * - the capacity readout is Lighthouse.Admin (it names the database's files) — an ordinary read;
 * - clearing a halt is Lighthouse.Admin AND pinned to an Entra sign-in (A4), so it is sent bearer-only:
 *   with no Entra token it throws BearerRequiredError before sending, and never falls back to the API key.
 */

const BASE = '/api/etl/halts'

/**
 * Halts, newest first. With `includeAcknowledged` false (the server's default) only the ones still blocking
 * runs — so an empty array answers "is anything halted right now".
 */
export function getEtlHalts(includeAcknowledged: boolean): Promise<EtlHalt[]> {
  return fetchApi<EtlHalt[]>(`${BASE}?includeAcknowledged=${includeAcknowledged}`)
}

/**
 * The warehouse's storage headroom now, judged with no projected load against the API's configured reserve.
 * A 403 for anyone the Admin policy refuses. `isSufficient: null` means nothing was measured.
 */
export function getEtlHaltCapacity(): Promise<EtlStorageCapacity> {
  return fetchApi<EtlStorageCapacity>(`${BASE}/capacity`)
}

/**
 * Clears a halt, with the note the server requires. A repeat clear answers `clearedByThisRequest: false` and
 * records nothing. Refusals keep the halt blocking: 400 (no note, or longer than the column), 401 (not an
 * Entra sign-in), 403 (no role, or no identifier the audit trail can record), 404 (no such halt).
 */
export function acknowledgeEtlHalt(runId: string, note: string): Promise<AcknowledgeEtlHaltResponse> {
  return fetchApi<AcknowledgeEtlHaltResponse>(`${BASE}/${encodeURIComponent(runId)}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({ note }),
    auth: 'bearer-only',
  })
}
